import { useEffect, useRef, useState } from "preact/hooks";
import { get, html, navigate, patch, post, relTime } from "../lib.js";
import { Badge, Empty, Field, Loading, PageHeader, toast, useAction, useForm, useLoad } from "../ui.js";
import { getLang, tr } from "../i18n.js";

/** Arrows that read right in both directions: "back" points toward the start of the line. */
const BACK = () => (getLang() === "he" ? "→" : "←");
const NEXT = () => (getLang() === "he" ? "←" : "→");
import { InviteOutcome } from "./admin.js";

// RELAYZA Academy: training and certification for RELAYZA's own reps and partners (src/academy).
// Lesson content is Hebrew; the chrome around it goes through tr() like every other page.

const TABS = [
  { key: "", label: "Overview" },
  { key: "sparring", label: "Sparring" },
  { key: "truth", label: "Product truth" },
  { key: "team", label: "Team", coach: true },
];

const STAGE = { sdr_training: "In SDR training", sdr_certified: "SDR certified", ae: "AE certified" };
const OUTCOME = { booked: "Call booked", handed_off: "Handed off", next_step: "Next step set", lost: "Lost", unfinished: "Unfinished" };
const VIOLATION = { price_in_writing: "Price in writing", false_claim: "Claim the product can't back", disrespect: "Pushy or disrespectful" };

/** The lesson markdown subset: "## " headings, "- " bullets, **bold**, blank-line paragraphs. */
function inline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part) => (part.startsWith("**") && part.endsWith("**") ? html`<strong>${part.slice(2, -2)}</strong>` : part));
}
function LessonBody({ body }) {
  return html`<div class="lesson-body" dir="rtl" lang="he">${body.split(/\n\s*\n/).map((block) => {
    const lines = block.split("\n").filter(Boolean);
    if (lines.every((l) => l.startsWith("- "))) return html`<ul>${lines.map((l) => html`<li>${inline(l.slice(2))}</li>`)}</ul>`;
    if (lines[0].startsWith("## ")) {
      const rest = lines.slice(1);
      return html`<h3>${lines[0].slice(3)}</h3>${rest.length ? (rest.every((l) => l.startsWith("- ")) ? html`<ul>${rest.map((l) => html`<li>${inline(l.slice(2))}</li>`)}</ul>` : html`<p>${inline(rest.join(" "))}</p>`) : ""}`;
    }
    return html`<p>${inline(lines.join(" "))}</p>`;
  })}</div>`;
}

function Meter({ label, done, need }) {
  const ok = done >= need;
  return html`<div class="card kpi">
    <div class="kpi-label">${label}</div>
    <div class="kpi-value">${Math.min(done, need)}<span class="muted small"> / ${need}</span></div>
    <div class="meter" role="progressbar" aria-valuemin="0" aria-valuemax=${need} aria-valuenow=${Math.min(done, need)} aria-label=${label}><span style=${`width:${Math.min(100, (done / need) * 100)}%`} class=${ok ? "ok" : ""}></span></div>
  </div>`;
}

export function AcademyPage({ path, me }) {
  const parts = path.split("/").filter(Boolean); // ["academy", ...]
  const [home, reload] = useLoad(() => get("/academy"), []);
  if (!home) return html`<${Loading} />`;
  const coach = home.role === "coach";
  const section = parts[1] ?? "";
  const tabKey = section === "m" ? "" : section;
  let body;
  if (section === "m") body = html`<${ModulePage} home=${home} moduleId=${parts[2]} reload=${reload} />`;
  else if (section === "sparring" && parts[2]) body = html`<${SparringSession} home=${home} id=${parts[2]} reload=${reload} />`;
  else if (section === "sparring") body = html`<${SparringHome} home=${home} />`;
  else if (section === "truth") body = html`<${TruthPage} />`;
  else if (section === "team" && coach && parts[2]) body = html`<${TraineeDetail} home=${home} userId=${parts[2]} />`;
  else if (section === "team" && coach) body = html`<${TeamPage} />`;
  else body = html`<${Overview} home=${home} me=${me} />`;
  return html`<div>
    <${PageHeader} title=${tr("RELAYZA Academy")} subtitle=${coach ? tr("Coach view: train and certify reps and partners") : tr("Training and certification for RELAYZA reps and partners")} />
    <div class="tabs">${TABS.filter((x) => !x.coach || coach).map((x) => html`<button class=${tabKey === x.key ? "active" : ""} onClick=${() => navigate(`/academy${x.key ? `/${x.key}` : ""}`)}>${tr(x.label)}</button>`)}</div>
    ${body}
  </div>`;
}

function Overview({ home }) {
  const s = home.standing;
  const done = new Set(s.lessons_done);
  return html`<div>
    <section class="card">
      <div class="actions" style="justify-content: space-between">
        <h2>${tr("Where you stand")}</h2>
        <${Badge} kind=${s.stage === "sdr_training" ? "accent" : "ok"}>${tr(STAGE[s.stage])}</${Badge}>
      </div>
      <p class="muted small">${tr("Pass the SDR gate to book discovery calls for the founder. The AE gate, with a coach's sign-off, lets you run discovery calls and close.")}</p>
      <h3>${tr("SDR gate")}</h3>
      <div class="kpis">
        <${Meter} label=${tr("Quizzes passed")} done=${s.sdr.quizzes_done} need=${s.sdr.quizzes_needed} />
        <${Meter} label=${tr("Sparring sessions passed")} done=${s.sdr.sessions_passed} need=${s.sdr.sessions_needed} />
        <${Meter} label=${tr("Different prospects")} done=${s.sdr.personas_passed} need=${s.sdr.personas_needed} />
      </div>
      <h3>${tr("AE gate")}</h3>
      ${s.sdr_certified
        ? html`<div class="kpis">
            <${Meter} label=${tr("Quizzes passed")} done=${s.ae.quizzes_done} need=${s.ae.quizzes_needed} />
            <${Meter} label=${tr("Discovery sessions passed")} done=${s.ae.sessions_passed} need=${s.ae.sessions_needed} />
            <${Meter} label=${tr("Coach sign-off")} done=${s.ae.certified_at ? 1 : 0} need=${1} />
          </div>
          ${s.ae.ready_for_signoff && !s.ae.certified_at && html`<div class="callout ok small">${tr("Ready for sign-off. Your coach will review your sessions.")}</div>`}`
        : html`<p class="muted small">${tr("Opens once you're SDR certified.")}</p>`}
    </section>

    <section>
      <h2>${tr("Modules")}</h2>
      <div class="grid-2">${home.modules.map((m) => {
        const lessonsDone = m.lessons.filter((l) => done.has(l.id)).length;
        const quiz = s.quizzes[m.id];
        return html`<a class="card module-card ${m.locked ? "locked" : ""}" href=${m.locked ? undefined : `/academy/m/${m.id}`} aria-disabled=${m.locked}>
          <div class="actions" style="justify-content: space-between"><${Badge} kind="neutral">${m.track.toUpperCase()}</${Badge}>
            ${m.locked ? html`<${Badge} kind="muted">${tr("Locked")}</${Badge}>` : quiz?.passed ? html`<${Badge} kind="ok">${tr("Passed {score}%", { score: quiz.best })}</${Badge}>` : ""}</div>
          <h3 dir="rtl" lang="he">${m.title}</h3>
          <p class="muted small" dir="rtl" lang="he">${m.goal}</p>
          <div class="muted small">${tr("{done} of {n} lessons", { done: lessonsDone, n: m.lessons.length })}</div>
        </a>`;
      })}</div>
    </section>

    <section class="card table-wrap">
      <h2>${tr("Your first 90 days")}</h2>
      <table class="table" dir="rtl" lang="he">
        <tbody>${home.ramp.map((r) => html`<tr><td><strong>${r.phase}</strong></td><td>${r.focus}</td><td class="muted">${r.outcome}</td></tr>`)}</tbody>
      </table>
    </section>
  </div>`;
}

function ModulePage({ home, moduleId, reload }) {
  const m = home.modules.find((x) => x.id === moduleId);
  const [open, setOpen] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, run] = useAction();
  if (!m) return html`<${Empty} title=${tr("Module not found")}><a href="/academy">${tr("Back to the Academy")}</a></${Empty}>`;
  if (m.locked) return html`<${Empty} title=${tr("Locked")}>${tr("Opens once you're SDR certified.")}</${Empty}>`;
  const done = new Set(home.standing.lessons_done);
  const complete = async (id) => {
    await run(() => post(`/academy/lessons/${id}/complete`));
    reload();
  };
  const submit = async (e) => {
    e.preventDefault();
    const res = await run(() => post(`/academy/quizzes/${m.id}`, { answers }));
    setResult(res);
    toast(res.passed ? tr("Passed with {score}%", { score: res.score }) : tr("{score}%. 80% passes; read the explanations and try again.", { score: res.score }), res.passed ? "ok" : "error");
    reload();
  };
  const byId = Object.fromEntries((result?.results ?? []).map((r) => [r.id, r]));
  return html`<div>
    <a href="/academy" class="link small">${BACK()} ${tr("Back to the Academy")}</a>
    <h2 dir="rtl" lang="he">${m.title}</h2>
    <p class="muted" dir="rtl" lang="he">${m.goal}</p>
    ${m.lessons.map((l) => html`<section class="card">
      <div class="actions" style="justify-content: space-between">
        <button class="link" aria-expanded=${open === l.id} onClick=${() => setOpen(open === l.id ? null : l.id)}><h3 dir="rtl" lang="he" style="margin:0">${l.title}</h3></button>
        <span class="actions"><span class="muted small">${tr("{n} min", { n: l.minutes })}</span>${done.has(l.id) && html`<${Badge} kind="ok">${tr("Done")}</${Badge}>`}</span>
      </div>
      ${open === l.id && html`<${LessonBody} body=${l.body} />
        ${!done.has(l.id) && html`<button class="btn" disabled=${busy} onClick=${() => complete(l.id)}>${tr("Mark as read")}</button>`}`}
    </section>`)}
    <form class="card" onSubmit=${submit}>
      <h2>${tr("Quiz")}</h2>
      <p class="muted small">${tr("80% passes. You can retake it; your best score counts.")}</p>
      ${m.quiz.map((q, i) => {
        const r = byId[q.id];
        return html`<fieldset class="quiz-q" dir="rtl" lang="he">
          <legend><strong>${i + 1}.</strong> ${q.q}</legend>
          ${q.options.map((o, j) => html`<label class="quiz-opt ${r ? (j === r.answer ? "right" : answers[q.id] === j ? "wrong" : "") : ""}">
            <input type="radio" name=${q.id} checked=${answers[q.id] === j} onChange=${() => { setAnswers({ ...answers, [q.id]: j }); setResult(null); }} /> ${o}
          </label>`)}
          ${r && html`<div class="callout small ${r.correct ? "ok" : "warn"}">${r.why}</div>`}
        </fieldset>`;
      })}
      <button class="btn primary" disabled=${busy || Object.keys(answers).length < m.quiz.length}>${tr("Submit answers")}</button>
    </form>
  </div>`;
}

function SparringHome({ home }) {
  const [list] = useLoad(() => get("/academy/sparring"), []);
  const [scenario, setScenario] = useState(home.scenarios.find((s) => !s.locked)?.id ?? "sdr_first_contact");
  const [busy, run] = useAction();
  const start = async (personaId) => {
    const s = await run(() => post("/academy/sparring", { scenario_id: scenario, persona_id: personaId }));
    navigate(`/academy/sparring/${s.id}`);
  };
  const sc = home.scenarios.find((s) => s.id === scenario);
  const personaName = Object.fromEntries(home.personas.map((p) => [p.id, `${p.name}, ${p.business}`]));
  return html`<div>
    <section class="card">
      <h2>${tr("Practice against a simulated prospect")}</h2>
      <p class="muted small">${tr("You play the RELAYZA rep. Claude plays a business owner with a hidden profile, then grades you against the rubric. {pass}% passes; a price in writing or a claim the product can't back fails the session.", { pass: home.pass_score })}</p>
      <div class="tabs">${home.scenarios.map((s) => html`<button class=${scenario === s.id ? "active" : ""} disabled=${s.locked} onClick=${() => setScenario(s.id)}>${s.title}${s.locked ? ` · ${tr("Locked")}` : ""}</button>`)}</div>
      <p dir="rtl" lang="he">${sc.task}</p>
      <div class="muted small">${tr("Graded on")}: ${sc.rubric.map((r) => `${r.label} (${r.weight})`).join(" · ")}</div>
    </section>
    <div class="grid-2">${home.personas.map((p) => html`<section class="card">
      <h3 dir="rtl" lang="he">${p.name}, ${p.business}</h3>
      <p class="muted small" dir="rtl" lang="he">${p.brief}</p>
      <button class="btn primary" disabled=${busy || sc.locked} onClick=${() => start(p.id)}>${tr("Start")}</button>
    </section>`)}</div>
    <section class="card table-wrap">
      <h2>${tr("Your sessions")}</h2>
      ${!list ? html`<${Loading} />` : !list.sessions.length ? html`<p class="muted">${tr("No sessions yet.")}</p>` : html`<table class="table">
        <thead><tr><th>${tr("Prospect")}</th><th>${tr("Scenario")}</th><th>${tr("Score")}</th><th>${tr("When")}</th></tr></thead>
        <tbody>${list.sessions.map((s) => html`<tr>
          <td><a href=${`/academy/sparring/${s.id}`} dir="auto">${personaName[s.persona_id] ?? s.persona_id}</a></td>
          <td class="small">${home.scenarios.find((x) => x.id === s.scenario_id)?.title}</td>
          <td>${s.status === "graded" ? html`<${Badge} kind=${s.passed ? "ok" : "warn"}>${s.score}</${Badge}>` : html`<${Badge} kind="neutral">${tr(s.status === "active" ? "In progress" : "Abandoned")}</${Badge}>`}</td>
          <td class="muted small">${relTime(s.created_at)}</td>
        </tr>`)}</tbody></table>`}
    </section>
  </div>`;
}

function SparringSession({ home, id, reload }) {
  const [session, setSession] = useState(null);
  const [text, setText] = useState("");
  const [ended, setEnded] = useState(false);
  const [busy, run] = useAction();
  const [thinking, setThinking] = useState(false);
  const bottom = useRef(null);
  useEffect(() => {
    get(`/academy/sparring/${id}`).then(setSession).catch((e) => toast(e.message, "error"));
  }, [id]);
  useEffect(() => bottom.current?.scrollIntoView({ block: "end" }), [session?.messages?.length, thinking]);
  if (!session) return html`<${Loading} />`;
  const persona = home.personas.find((p) => p.id === session.persona_id);
  const scenario = home.scenarios.find((s) => s.id === session.scenario_id);
  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setText("");
    setSession({ ...session, messages: [...session.messages, { role: "rep", text: body, at: new Date().toISOString() }] });
    setThinking(true);
    try {
      const res = await run(() => post(`/academy/sparring/${id}/messages`, { text: body }));
      setSession((s) => ({ ...s, messages: res.messages }));
      if (res.ended) setEnded(true);
    } catch {
      setText(body);
      get(`/academy/sparring/${id}`).then(setSession);
    } finally {
      setThinking(false);
    }
  };
  const finish = async () => {
    setThinking(true);
    try {
      const res = await run(() => post(`/academy/sparring/${id}/finish`));
      setSession(res);
      reload();
    } finally {
      setThinking(false);
    }
  };
  const active = session.status === "active";
  return html`<div>
    <a href="/academy/sparring" class="link small">${BACK()} ${tr("All sessions")}</a>
    <div class="inbox-thread sparring">
      <header class="thread-head">
        <div><h2 dir="rtl" lang="he">${persona?.name}, ${persona?.business}</h2><div class="muted small">${scenario?.title}</div></div>
        ${active && html`<button class="btn ${ended ? "primary" : ""}" disabled=${busy} onClick=${finish}>${tr("Finish and grade")}</button>`}
      </header>
      <div class="messages" aria-live="polite">
        ${session.messages.map((m) => html`<div class="msg ${m.role === "rep" ? "out" : "in"}">
          <div class="bubble" dir="auto">${m.text}</div>
          <div class="meta">${m.role === "rep" ? tr("You") : persona?.name}</div>
        </div>`)}
        ${thinking && html`<div class="msg in"><div class="bubble"><span class="typing" aria-label=${tr("Typing…")}><span></span><span></span><span></span></span></div></div>`}
        <div ref=${bottom}></div>
      </div>
      ${active && ended && html`<div class="callout ok small">${tr("The prospect wrapped up. Finish to get your grade.")}</div>`}
      ${active && html`<form class="composer" onSubmit=${send}>
        <textarea dir="auto" rows="2" maxlength="2000" aria-label=${tr("Your reply")} placeholder=${tr("Write as the RELAYZA rep")} value=${text}
          onInput=${(e) => setText(e.target.value)} onKeyDown=${(e) => e.key === "Enter" && !e.shiftKey && send(e)}></textarea>
        <button class="btn primary" disabled=${busy || !text.trim()}>${tr("Send")}</button>
      </form>`}
    </div>
    ${session.grade && html`<${GradeReport} session=${session} />`}
  </div>`;
}

function GradeReport({ session }) {
  const g = session.grade;
  return html`<section class="card grade">
    <div class="actions" style="justify-content: space-between">
      <h2>${tr("Your grade")}</h2>
      <span class="actions"><${Badge} kind="neutral">${tr(OUTCOME[g.outcome] ?? g.outcome)}</${Badge}><span class="kpi-value ${session.passed ? "pass" : "fail"}">${session.score}</span></span>
    </div>
    <p dir="rtl" lang="he">${g.summary}</p>
    ${g.violations.length > 0 && html`<div class="callout error">
      <strong>${tr("This failed the session")}</strong>
      <ul dir="rtl" lang="he">${g.violations.map((v) => html`<li><strong>${tr(VIOLATION[v.type] ?? v.type)}:</strong> "${v.quote}". ${v.explanation}</li>`)}</ul>
    </div>`}
    <table class="table" dir="rtl" lang="he"><tbody>${g.criteria.map((c) => html`<tr>
      <td><strong>${c.label}</strong><div class="muted small">${c.evidence}</div></td>
      <td class="nowrap" aria-label=${tr("{mark} of 5", { mark: c.mark })}>${"●".repeat(c.mark)}<span class="muted">${"○".repeat(5 - c.mark)}</span></td>
    </tr>`)}</tbody></table>
    <div class="grid-2" dir="rtl" lang="he">
      <div><h3>${tr("What worked")}</h3><ul>${g.strengths.map((s) => html`<li>${s}</li>`)}</ul></div>
      <div><h3>${tr("Try this instead")}</h3><ul>${g.improvements.map((i) => html`<li><span class="muted">${i.moment}</span><br />${NEXT()} ${i.better}</li>`)}</ul></div>
    </div>
    ${session.coach_note && html`<div class="callout"><strong>${tr("Coach's note")}:</strong> <span dir="auto">${session.coach_note}</span></div>`}
  </section>`;
}

function TruthPage() {
  const [data] = useLoad(() => get("/academy/truth"), []);
  if (!data) return html`<${Loading} />`;
  return html`<div>
    <div class="callout small">${tr("What RELAYZA does today, from the same source the chat on relayza.company answers from. When in doubt, check here before you answer. The sparring grader holds you to it.")}</div>
    ${!data.items.length
      ? html`<${Empty} title=${tr("Not connected here")}>${tr("The capability index isn't published on this environment. Use docs/capabilities in the repository.")}</${Empty}>`
      : data.items.map((i) => html`<details class="card"><summary><strong>${i.title}</strong></summary><div class="pre-wrap" dir="auto">${i.content}</div></details>`)}
  </div>`;
}

function TeamPage() {
  const [data, reload] = useLoad(() => get("/academy/team"), []);
  const { values, bind, setValues } = useForm({ name: "", email: "", kind: "rep", lang: "he" });
  const [invite, setInvite] = useState(null);
  const [busy, run] = useAction();
  const submit = async (e) => {
    e.preventDefault();
    const res = await run(() => post("/academy/team", values), tr("Invited"));
    setInvite(res.invite);
    setValues({ name: "", email: "", kind: "rep", lang: "he" });
    reload();
  };
  return html`<div class="grid-agent">
    <section class="card table-wrap">
      ${!data ? html`<${Loading} />` : !data.members.length ? html`<p class="muted">${tr("Nobody in the Academy yet. Invite your first rep.")}</p>` : html`<table class="table">
        <thead><tr><th>${tr("Name")}</th><th>${tr("Stage")}</th><th>${tr("Sessions")}</th><th>${tr("Average score")}</th><th>${tr("Last practice")}</th></tr></thead>
        <tbody>${data.members.map((m) => html`<tr>
          <td><a href=${`/academy/team/${m.user_id}`}><strong>${m.name}</strong></a><div class="muted small">${m.email} · ${tr(m.kind === "partner" ? "Partner" : "Rep")}${m.status === "archived" ? ` · ${tr("Archived")}` : ""}</div></td>
          <td><${Badge} kind=${m.standing.stage === "sdr_training" ? "accent" : "ok"}>${tr(STAGE[m.standing.stage])}</${Badge}>
            ${m.standing.ae.ready_for_signoff && !m.standing.ae.certified_at && html` <${Badge} kind="warn">${tr("Awaiting sign-off")}</${Badge}>`}</td>
          <td>${m.sessions}</td><td>${m.avg_score ?? "—"}</td>
          <td class="muted small">${m.last_session_at ? relTime(m.last_session_at) : "—"}</td>
        </tr>`)}</tbody></table>`}
    </section>
    <form class="card" onSubmit=${submit}>
      <h2>${tr("Invite a trainee")}</h2>
      <${Field} label=${tr("Name")}><input required ...${bind("name")} /></${Field}>
      <${Field} label=${tr("Email")}><input type="email" required ...${bind("email")} /></${Field}>
      <${Field} label=${tr("Type")}><select ...${bind("kind")}><option value="rep">${tr("Rep")}</option><option value="partner">${tr("Partner")}</option></select></${Field}>
      <${Field} label=${tr("Invite language")}><select ...${bind("lang")}><option value="he">עברית</option><option value="en">English</option></select></${Field}>
      <button class="btn primary" disabled=${busy}>${tr("Send invite")}</button>
      ${invite && html`<${InviteOutcome} invite=${invite} />`}
    </form>
  </div>`;
}

function TraineeDetail({ home, userId }) {
  const [data, reload] = useLoad(() => get(`/academy/team/${userId}`), [userId]);
  const [notes, setNotes] = useState({});
  const [busy, run] = useAction();
  if (!data) return html`<${Loading} />`;
  const s = data.standing;
  const personaName = Object.fromEntries(home.personas.map((p) => [p.id, `${p.name}, ${p.business}`]));
  const signoff = async () => {
    await run(() => post(`/academy/team/${userId}/signoff`), tr("AE certified"));
    reload();
  };
  const archive = async () => {
    await run(() => patch(`/academy/team/${userId}`, { status: data.member.status === "active" ? "archived" : "active" }));
    reload();
  };
  const saveNote = (id) => run(() => patch(`/academy/sparring/${id}/note`, { note: notes[id] ?? "" }), tr("Saved"));
  return html`<div>
    <a href="/academy/team" class="link small">${BACK()} ${tr("Team")}</a>
    <section class="card">
      <div class="actions" style="justify-content: space-between">
        <h2>${data.member.name}</h2>
        <span class="actions"><${Badge} kind="ok">${tr(STAGE[s.stage])}</${Badge}>
          <button class="btn small ghost" disabled=${busy} onClick=${archive}>${data.member.status === "active" ? tr("Archive") : tr("Reactivate")}</button></span>
      </div>
      <div class="kpis">
        <${Meter} label=${tr("SDR quizzes")} done=${s.sdr.quizzes_done} need=${s.sdr.quizzes_needed} />
        <${Meter} label=${tr("SDR sessions")} done=${s.sdr.sessions_passed} need=${s.sdr.sessions_needed} />
        <${Meter} label=${tr("AE quizzes")} done=${s.ae.quizzes_done} need=${s.ae.quizzes_needed} />
        <${Meter} label=${tr("Discovery sessions")} done=${s.ae.sessions_passed} need=${s.ae.sessions_needed} />
      </div>
      ${s.ae.ready_for_signoff && !s.ae.certified_at && html`<div class="callout ok">
        ${tr("Met every AE requirement. Read the discovery sessions below, then sign off.")}
        <div class="form-actions"><button class="btn primary" disabled=${busy} onClick=${signoff}>${tr("Sign off as AE")}</button></div>
      </div>`}
    </section>
    ${!data.sessions.length && html`<p class="muted">${tr("No graded sessions yet.")}</p>`}
    ${data.sessions.map((x) => html`<details class="card">
      <summary class="actions" style="justify-content: space-between">
        <span dir="auto"><strong>${personaName[x.persona_id]}</strong> · ${home.scenarios.find((sc) => sc.id === x.scenario_id)?.title}</span>
        <${Badge} kind=${x.passed ? "ok" : "warn"}>${x.score}</${Badge}>
      </summary>
      <div class="messages coach-transcript">${x.messages.map((m) => html`<div class="msg ${m.role === "rep" ? "out" : "in"}"><div class="bubble" dir="auto">${m.text}</div></div>`)}</div>
      <${GradeReport} session=${{ ...x, coach_note: null }} />
      <${Field} label=${tr("Coach's note (the trainee sees it)")}>
        <textarea dir="auto" rows="2" value=${notes[x.id] ?? x.coach_note ?? ""} onInput=${(e) => setNotes({ ...notes, [x.id]: e.target.value })}></textarea>
      </${Field}>
      <button class="btn small" disabled=${busy} onClick=${() => saveNote(x.id)}>${tr("Save note")}</button>
    </details>`)}
  </div>`;
}
